import { restClient } from '#app/shared/apis/restClient'
import type { AuthContextResult } from '#app/shared/contexts/AuthContext'
import { sanitizeEventProperties } from '#app/shared/utils/eventUtils'
import { deviceType, getUA, osName, osVersion } from 'react-device-detect'
import { publishFSCustomEvent } from './fullstory'
import { publishPostHogEvent } from './posthog'

/**
 * Default event mapping item, fallback when specific event type is not found in the dispatch map.
 */
export const DefaultMapItem = '*'

export type EventData = Record<
  string,
  number | boolean | string | object | undefined
>

/**
 * Input type that allows null values, which will be converted to undefined
 */
export type EventDataInput = Record<
  string,
  number | boolean | string | object | undefined | null
>

/**
 * Recursively removes null values from an object, converting them to undefined
 */
const removeNullValues = (obj: unknown): unknown => {
  if (obj === null) {
    return undefined
  }

  if (Array.isArray(obj)) {
    return obj.map(removeNullValues)
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeNullValues(value)
      if (cleanedValue !== undefined) {
        result[key] = cleanedValue
      }
    }
    return result
  }

  return obj
}

/**
 * Event types defined by TrueNorth Event ingestion pipeline.
 * Follow best practice where event names are upppercase with underscores.
 */
export enum EventTypes {
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  USER_DEEP_LINKED = 'USER_DEEP_LINKED',
  NOTIFICATION_TAPPED = 'NOTIFICATION_TAPPED',
  /**
   * A carrier was refused a private loadboard named in the URL.
   *
   * This exists to be near-zero. The page once compared the whole query string
   * against the carrier's board slugs, so a recommendations `?token=` read as an
   * unknown board and locked the carrier out of their own recommendations --
   * silently, for three months. A counter on the refusal makes that class of
   * regression visible instead of inferable.
   */
  PRIVATE_LOADBOARD_ACCESS_REFUSED = 'PRIVATE_LOADBOARD_ACCESS_REFUSED'
}

/**
 * Base event type defined by TrueNorth Event ingestion pipeline.
 */
export interface EventBase {
  /**
   * The event version. Default is 1.
   */
  version?: number
  /**
   * The event type name. Should be uppercase with underscores.
   */
  eventType: string
  /**
   * The event data.
   */
  eventData?: EventDataInput
  /**
   * Time the event occurred. If not set, is automatically assigned.
   */
  timestamp?: number
  /**
   * The device info.
   */
  deviceInfo?: {
    platform: string
    deviceType: string
    osName: string
    osVersion: string
    userAgent: string
  }
}

/**
 * This dispatch configuration mapping between the Event's `eventType` and which services that event should be sent to.
 */
export type EventDispatchMap = {
  [key: string]: {
    /** Event is sent to Intercom */
    intercom: boolean
    /** Event is sent to FullStory */
    fullstory: boolean
    /** Event is sent to TN Event Ingestion pipline */
    ingestion: boolean
  }
}

export const generalEventDispatchMap: EventDispatchMap = {
  // '*' means default mapping
  [DefaultMapItem]: {
    intercom: false, // by default DO NOT send to Intercom
    fullstory: true,
    ingestion: true
  },
  [EventTypes.USER_LOGGED_IN]: {
    intercom: true,
    fullstory: true,
    ingestion: true
  },
  [EventTypes.USER_LOGGED_OUT]: {
    intercom: true,
    fullstory: true,
    ingestion: true
  }
}

export type EventProcessingOptions = {
  dispatchMap: EventDispatchMap
  /**
   * Map of event types to FullStory event types.
   * Use this override only when FullStory is expecting event type names different from our standard.
   * Only use this mapping when trying to leverage publishEvent pattern for existing events in FullStory which are not following latest event naming standard.
   */
  fullstoryEventTypeMap?: { [key: string]: string }
}

/**
 * Global default event processing options.
 */
export const generalEventProcessingOptions: EventProcessingOptions = {
  dispatchMap: generalEventDispatchMap
}

/**
 * The user that is currently in scope for event publishing.
 */
let userInScope: AuthContextResult | null = null

export const identifyUser = (user: AuthContextResult | null) => {
  userInScope = user
}

/**
 * Logs out the current user from Event Publishing.
 */
export const logoutUser = () => {
  userInScope = null
}

export const publishSimpleEvent = async (
  eventType: string,
  options = generalEventProcessingOptions
) => {
  await publishEvent(
    {
      eventType
    },
    options
  )
}

export const publishEvent = async (
  event: EventBase,
  options: EventProcessingOptions = generalEventProcessingOptions
) => {
  const { dispatchMap, fullstoryEventTypeMap } = options
  const { eventType, eventData, timestamp } = event

  if (!timestamp) {
    event.timestamp = Date.now()
  }

  const eventDataWithDeviceInfo = {
    ...eventData,
    appIdentifier: 'web',
    deviceInfo: {
      platform: 'web',
      deviceType,
      osName,
      osVersion,
      userAgent: getUA
    }
  }

  const mapItem = dispatchMap[eventType] ?? dispatchMap[DefaultMapItem]
  // Convert null values to undefined to satisfy EventData type
  const sanitizedEventData = sanitizeEventProperties(
    removeNullValues(eventDataWithDeviceInfo ?? {}) as EventData
  )

  const { intercom, fullstory, ingestion } = mapItem

  if (intercom) {
    // prefix the event to distinguish from other events in Intercom
    Intercom('trackEvent', `TRM App: ${eventType}`, sanitizedEventData)
  }

  if (fullstory) {
    let fsEventType = eventType
    if (fullstoryEventTypeMap?.[eventType]) {
      fsEventType = fullstoryEventTypeMap[eventType]
    }
    publishFSCustomEvent(fsEventType, sanitizedEventData ?? {})
    publishPostHogEvent(fsEventType, sanitizedEventData ?? {})
  }

  if (ingestion) {
    const sanitizedEvent = {
      ...event,
      eventData: sanitizedEventData
    }

    await sendIngestionEvent(sanitizedEvent)
  }
}

/**
 * Sends an event to the TN event ingestion service.
 * @param event
 * @returns
 */
const sendIngestionEvent = async (event: EventBase): Promise<void> => {
  try {
    // TODO: error handling/retries/return value, etc
    await restClient.post('/events', {
      version: 1, // default version unless overridden by event
      ...event,
      userId: userInScope?.userKey || null,
      user: userInScope?.authenticatedUser || null
    })
  } catch (e) {
    const errorMsg = 'Failed to send event to event ingestion service'
    console.error(errorMsg, e)
  }
}
